/**
 * Gemini `usageMetadata` → billable token counts. The single reader every
 * cost path uses, so thinking tokens are counted the same way everywhere.
 *
 * BILLING RULE — thinking tokens are billed at the OUTPUT rate:
 *   - AI Studio: "When thinking is turned on, response pricing is the sum of
 *     output tokens and thinking tokens."
 *     https://ai.google.dev/gemini-api/docs/thinking (page updated 2026-09-25,
 *     read 2026-09-30); every paid row on
 *     https://ai.google.dev/gemini-api/docs/pricing reads "Output price
 *     (including thinking tokens)".
 *   - Vertex: the output row for every Gemini model reads "Text output
 *     (response and reasoning)" and points at the `thoughts` usage field.
 *     https://cloud.google.com/vertex-ai/generative-ai/pricing (read 2026-09-30).
 *
 * FIELD SEMANTICS (@google/genai GenerateContentResponseUsageMetadata):
 *   - `candidatesTokenCount` — visible response tokens only; EXCLUDES thoughts.
 *   - `thoughtsTokenCount`   — thinking tokens (3.x and 2.5 thinking models).
 *   - `totalTokenCount`      — prompt + candidates + toolUsePrompt + thoughts.
 * Before 2026-09-30 every cost path read `candidatesTokenCount` only, so a
 * thinking model's reasoning output was never priced.
 *
 * Pure (no imports): shared by the browser tracer, the report worker, the
 * server chat client and the `functions/` Ask DAM callable.
 */

/** Token counts read from one Gemini response's `usageMetadata`. */
export interface GeminiUsageCounts {
  /** `promptTokenCount` — billed at the input rate. */
  promptTokens: number;
  /** `candidatesTokenCount` (or legacy `responseTokenCount`) — visible response only. */
  candidatesTokens: number;
  /** `thoughtsTokenCount` — thinking tokens, billed at the output rate. */
  thoughtsTokens: number;
  /** candidatesTokens + thoughtsTokens — the count to price at the output rate. */
  billableOutputTokens: number;
  /** `totalTokenCount`, or prompt + candidates + thoughts when absent. */
  totalTokens: number;
}

/**
 * Measured usage handed to `onUsage` callbacks and economic-research cost
 * fields. `outputTokens` is the BILLABLE output count (response + thinking).
 */
export interface GeminiMeasuredTokenUsage {
  inputTokens: number;
  outputTokens: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * A finite, non-negative token count, or undefined when not reported.
 * Numeric strings are coerced (the pre-2026-09-30 readers used `Number()`).
 */
function reportedCount(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Strict variant for measured usage: only a real finite, non-negative number counts. */
function measuredCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * Read a response's `usageMetadata` into billable counts. Missing or
 * malformed fields read as 0 (never NaN), so a cost sum cannot be poisoned.
 */
export function readGeminiUsage(usageMetadata: unknown): GeminiUsageCounts {
  const u = asRecord(usageMetadata) ?? {};
  const promptTokens = reportedCount(u.promptTokenCount) ?? 0;
  const candidatesTokens =
    reportedCount(u.candidatesTokenCount) ?? reportedCount(u.responseTokenCount) ?? 0;
  const thoughtsTokens = reportedCount(u.thoughtsTokenCount) ?? 0;
  const billableOutputTokens = candidatesTokens + thoughtsTokens;
  const totalTokens =
    reportedCount(u.totalTokenCount) ?? promptTokens + billableOutputTokens;
  return { promptTokens, candidatesTokens, thoughtsTokens, billableOutputTokens, totalTokens };
}

/**
 * Measured-only variant: returns null when the response reported none of the
 * prompt / candidates / thoughts counts, so callers never record an estimate
 * in place of provider metadata. `outputTokens` includes thinking tokens.
 */
export function measuredGeminiTokenUsage(usageMetadata: unknown): GeminiMeasuredTokenUsage | null {
  const u = asRecord(usageMetadata);
  if (!u) return null;
  const input = measuredCount(u.promptTokenCount);
  const candidates = measuredCount(u.candidatesTokenCount);
  const thoughts = measuredCount(u.thoughtsTokenCount);
  if (input === undefined && candidates === undefined && thoughts === undefined) return null;
  return {
    inputTokens: input ?? 0,
    outputTokens: (candidates ?? 0) + (thoughts ?? 0),
  };
}
